import { getToastAction, type ToastVariant } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { Toast } from "@base-ui/react/toast";
import { Check, CircleCheck, CircleX, Copy, Info, TriangleAlert, X } from "lucide-react";
import { useRef, useState } from "react";

// Legacy parity: the old MUI `Alert` had success / info / warning / error
// severities with a type icon, fixed at the top of the viewport. We keep the
// four types but on a LIGHT tinted background with dark text (Nick, 2026-09-25)
// instead of legacy's solid fills; dark mode inverts to a deep tint + light text.
const VARIANT_CLASSES: Record<ToastVariant, string> = {
  default: "border-border bg-background text-foreground",
  success:
    "border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-100",
  info: "border-sky-300 bg-sky-50 text-sky-900 dark:border-sky-800 dark:bg-sky-950 dark:text-sky-100",
  warning:
    "border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100",
  destructive:
    "border-red-300 bg-red-50 text-red-900 dark:border-red-800 dark:bg-red-950 dark:text-red-100",
};

const VARIANT_ICON: Record<ToastVariant, typeof Info | null> = {
  default: null,
  success: CircleCheck,
  info: Info,
  warning: TriangleAlert,
  destructive: CircleX,
};

const VARIANT_ICON_CLASSES: Record<ToastVariant, string> = {
  default: "",
  success: "text-emerald-600 dark:text-emerald-400",
  info: "text-sky-600 dark:text-sky-400",
  warning: "text-amber-600 dark:text-amber-400",
  destructive: "text-red-600 dark:text-red-400",
};

function isToastVariant(type: string | undefined): type is ToastVariant {
  return type !== undefined && type in VARIANT_CLASSES;
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      data-base-ui-swipe-ignore
      title="Copy error"
      className="absolute right-8 top-2 rounded-md p-1 opacity-0 transition-opacity hover:opacity-100 focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-current/40 group-hover:opacity-60"
      onClick={async () => {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
    >
      {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
    </button>
  );
}

export function Toaster() {
  const manager = Toast.useToastManager();
  const { toasts } = manager;
  // Pointer-down coords of the current press on a toast root, so a completed
  // or aborted swipe (Base UI swipe-to-dismiss fires a browser click on
  // release) and text-selection drags don't count as action clicks.
  const pressStart = useRef<{ x: number; y: number } | null>(null);

  return (
    <Toast.Portal>
      {/* Top-center on every viewport (legacy `.bday-alert--top`). Bottom
          placement on mobile was considered and rejected: the software
          keyboard hides a bottom toast exactly when form-error toasts fire.
          Several toasts STACK (newest in front, older ones peeking below, expanding on
          hover) — the `.bday-toast` rules in index.css; the roots are absolute inside
          this box. */}
      <Toast.Viewport className="fixed top-3 left-1/2 z-100 w-[calc(100%-1.5rem)] max-w-[24.25rem] -translate-x-1/2 sm:top-4">
        {toasts.map((toast) => {
          const variant: ToastVariant = isToastVariant(toast.type) ? toast.type : "default";
          const isDestructive = variant === "destructive";
          const Icon = VARIANT_ICON[variant];
          const copyText = [toast.title, toast.description]
            .filter((x): x is string => typeof x === "string")
            .join(": ");
          // Optional whole-toast click action (use-toast `onClick`) — e.g. a
          // notification deep link. Inner buttons (close/copy) keep their own
          // behavior; a click on them must not also fire the action.
          const onClickAction = getToastAction(toast);
          const runAction = () => {
            onClickAction?.();
            manager.close(toast.id);
          };
          const handleRootClick = onClickAction
            ? (e: React.MouseEvent) => {
                if ((e.target as HTMLElement).closest("button")) return;
                // A drag (swipe-to-dismiss or text selection) that ends on the
                // toast still synthesizes a click — ignore moved presses.
                const start = pressStart.current;
                if (start && Math.hypot(e.clientX - start.x, e.clientY - start.y) > 8) return;
                if (!window.getSelection()?.isCollapsed) return;
                runAction();
              }
            : undefined;
          return (
            <Toast.Root
              key={toast.id}
              toast={toast}
              swipeDirection={["up", "left", "right"]}
              onClick={handleRootClick}
              onPointerDown={
                onClickAction
                  ? (e: React.PointerEvent) => {
                      pressStart.current = { x: e.clientX, y: e.clientY };
                    }
                  : undefined
              }
              // Keyboard path for the action (toasts are focusable via Base UI's
              // viewport hotkey); Enter mirrors the click.
              role={onClickAction ? "button" : undefined}
              tabIndex={onClickAction ? 0 : undefined}
              onKeyDown={
                onClickAction
                  ? (e: React.KeyboardEvent) => {
                      if (e.target !== e.currentTarget) return;
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        runAction();
                      }
                    }
                  : undefined
              }
              className={cn(
                onClickAction && "cursor-pointer",
                "bday-toast group flex items-start gap-3 overflow-hidden rounded-lg border px-4 py-3 pr-10 shadow-lg",
                isDestructive && "pr-16",
                VARIANT_CLASSES[variant],
              )}
            >
              {Icon && (
                <Icon
                  aria-hidden
                  className={cn("mt-0.5 h-5 w-5 shrink-0", VARIANT_ICON_CLASSES[variant])}
                />
              )}
              <Toast.Content className="bday-toast-content min-w-0 flex-1">
                <div className="grid gap-0.5">
                  {toast.title && (
                    <Toast.Title className="text-base font-semibold">{toast.title}</Toast.Title>
                  )}
                  {toast.description && (
                    <Toast.Description className="text-sm opacity-90">
                      {toast.description}
                    </Toast.Description>
                  )}
                </div>
              </Toast.Content>
              {isDestructive && copyText && <CopyButton text={copyText} />}
              <Toast.Close className="absolute right-2 top-2 rounded-md p-1 opacity-60 transition-opacity hover:opacity-100 focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-current/40">
                <X className="h-4 w-4" />
              </Toast.Close>
            </Toast.Root>
          );
        })}
      </Toast.Viewport>
    </Toast.Portal>
  );
}
